import React from 'react'
import * as PhosphorIcons from '@phosphor-icons/react'

const ToneAnalyzerViewResult = ({result}) => {
  const getToneColor = (tone) => {
    switch(tone?.toLowerCase()) {
      case 'positive':
      case 'strong':
        return 'bg-success/20 text-success';
      case 'neutral':
      case 'moderate':
        return 'bg-info/20 text-info';
      case 'negative':
      case 'weak':
        return 'bg-error/20 text-error';
      default:
        return 'bg-base-200 text-base-content';
    }
  };

  const getStyleIcon = (style) => {
    switch(style?.toLowerCase()) {
      case 'formal':
        return 'Briefcase';
      case 'casual':
        return 'Chat';
      case 'informative':
        return 'Book';
      default:
        return 'Article';
    }
  };

  return (
    <div className="mt-4 overflow-x-auto">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Tone Analysis Section */}
        <div className="space-y-4">
          {/* Tone Status Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-primary/10">
                  <PhosphorIcons.SpeakerHigh size={20} weight="bold" className="text-primary"/>
                </div>
                <h3 className="card-title text-lg">Tone Analysis</h3>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {/* Sentiment */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <span className="text-sm text-base-content/70">Sentiment</span>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${getToneColor(result?.analysis?.tone_status?.sentiment)}`}>
                      {result?.analysis?.tone_status?.sentiment}
                    </span>
                  </div>
                </div>

                {/* Style Match */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <span className="text-sm text-base-content/70">Style</span>
                  <div className="flex items-center gap-2 mt-1">
                    {PhosphorIcons[getStyleIcon(result?.analysis?.tone_status?.style_match)] && 
                      React.createElement(PhosphorIcons[getStyleIcon(result?.analysis?.tone_status?.style_match)], {
                        size: 16,
                        className: "text-primary"
                      })
                    }
                    <span className="capitalize">{result?.analysis?.tone_status?.style_match}</span>
                  </div>
                </div>

                {/* Brand Alignment */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <span className="text-sm text-base-content/70">Brand Alignment</span>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${getToneColor(result?.analysis?.tone_status?.brand_alignment)}`}>
                      {result?.analysis?.tone_status?.brand_alignment}
                    </span>
                  </div>
                </div>

                {/* Emotional Resonance */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <span className="text-sm text-base-content/70">Emotional Resonance</span>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="capitalize">{result?.analysis?.tone_status?.emotional_resonance}</span>
                  </div>
                </div>
              </div>

              {/* Improvement Status */}
              <div className="mt-3 p-3 bg-base-200 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="font-medium">Content Status</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs ${
                    result?.analysis?.tone_status?.improvement_needed
                      ? 'bg-warning/20 text-warning'
                      : 'bg-success/20 text-success'
                  }`}>
                    {result?.analysis?.tone_status?.improvement_needed ? 'Needs Improvement' : 'Optimized'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Validation Section */}
        <div className="space-y-4">
          {/* Validation Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-secondary/10">
                  <PhosphorIcons.CheckCircle size={20} weight="bold" className="text-secondary"/>
                </div>
                <h3 className="card-title text-lg">Validation</h3>
              </div>

              <div className="space-y-3">
                {/* Validation Status */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Status</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                      result?.analysis?.validation?.is_valid
                        ? 'bg-success/20 text-success'
                        : 'bg-error/20 text-error'
                    }`}>
                      {result?.analysis?.validation?.is_valid ? 'Valid' : 'Invalid'}
                    </span>
                  </div>
                </div>

                {/* Tone Type */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Tone Type</span>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-primary/20 text-primary capitalize">
                      {result?.analysis?.validation?.tone_type}
                    </span>
                  </div>
                </div>

                {/* Quality Score */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium">Quality Score</span>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-success/20 text-success">
                      {Math.round(result?.analysis?.validation?.quality_score * 100)}%
                    </span>
                  </div>
                  <div className="w-full bg-base-300 rounded-full h-2">
                    <div 
                      className="h-2 rounded-full bg-success"
                      style={{width: `${result?.analysis?.validation?.quality_score * 100}%`}}
                    ></div>
                  </div>
                </div>

                {/* Confidence */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium">Confidence</span>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-info/20 text-info">
                      {Math.round(result?.analysis?.validation?.confidence * 100)}%
                    </span>
                  </div>
                  <div className="w-full bg-base-300 rounded-full h-2">
                    <div 
                      className="h-2 rounded-full bg-info"
                      style={{width: `${result?.analysis?.validation?.confidence * 100}%`}}
                    ></div>
                  </div>
                </div>


              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ToneAnalyzerViewResult;